/* eslint-disable react-i18n/no-dynamic-translation-keys */
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import * as SC from "./AudioGenerator.styled.js";
import Select from "react-select";
import LoadingIndicator from "react-loading-indicator";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import logger from "../../../../../utils/LoggerFrontend.js";
import Actions from "../../../../../Actions.js";
import {
  DEFAULT_FORMALITIES,
  DEFAULT_GENERATION_ERROR,
  DEFAULT_JINGLES,
  DEFAULT_LANGUAGES,
  DEFAULT_SOUNDS,
  ERROR_MAPPING
} from "./AudioGenerator.constants.jsx";
import { useTranslation } from "react-i18next";
import { Tooltip } from "../../../../../Components/Overlays";
import withRefluxStore from "../../../../../Stores/withRefluxStore.jsx";
import PreviewStore from "../../../../../Stores/PreviewStore.js";
import CampaignCreationStore from "../../../../../Stores/CampaignCreationStore.js";
import { Button } from "../../../../../Components/Button";
import { Upload } from "../../../../../Components/Upload";
import { getLanguageByLocale } from "../../../../../PagesUser/ad-assistant/utils";

const limit = 500;

const AudioGenerator = ({
  orgaId,
  campaignId,
  updateAudioData,
  url,
  music,
  sound,
  audioText,
  channel,
  channelData,
  isAutoGenerated,
  autoGeneratedText,
  isAutoGenerationStreaming,
  useGen,
  setUseGen = () => {},
  // Store
  isRegeneratingText,
  userProfile,
  locale,
  campaignData
}) => {
  const { t } = useTranslation();
  const [prevValues, setPrevValues] = useState({
    music: "",
    sound: "",
    audioText: "",
    url: "",
    language: "",
    formality: ""
  });

  logger.log("AudioGenerator:constructor", "called", {
    orgaId,
    campaignId,
    updateAudioData,
    url,
    music,
    sound,
    audioText,
    channel,
    channelData,
    isAutoGenerated,
    autoGeneratedText,
    isAutoGenerationStreaming,
    isRegeneratingText,
    locale,
    campaignData
  });

  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  // Get the language based on locale
  const defaultLanguage = useMemo(() => {
    const campaignLanguage = getLanguageByLocale(campaignData?.locale || locale || "de_DE");
    return (
      DEFAULT_LANGUAGES.find((lang) => lang.label === campaignLanguage) || DEFAULT_LANGUAGES[0]
    );
  }, [locale, campaignData]);

  // Initialize language with the default based on locale
  const [languageValue, setLanguageValue] = useState(defaultLanguage);
  const [formality, setFormality] = useState(DEFAULT_FORMALITIES[0]);

  const audioRef = useRef(null);

  useEffect(() => {
    const audioRefCopy = audioRef;
    return function stopAudio() {
      if (audioRefCopy?.current) {
        audioRefCopy.current.pause();
      }
    };
  }, []);

  let textState = useGen ? autoGeneratedText : audioText;

  useEffect(() => {
    if (isAutoGenerated && !autoGeneratedText && !audioText) {
      regenerateText();
    }
  }, [isAutoGenerated, autoGeneratedText, audioText, regenerateText]);

  useEffect(() => {
    if (isRegeneratingText || (autoGeneratedText && isAutoGenerated && !audioText)) {
      setUseGen(true);
    }
  }, [isRegeneratingText, autoGeneratedText, isAutoGenerated, audioText, setUseGen]);

  useEffect(() => {
    if (!isAutoGenerationStreaming && useGen) {
      updateAudioData({ key: "audioText", value: autoGeneratedText });
    }
  }, [autoGeneratedText, isAutoGenerationStreaming, updateAudioData, useGen]);

  const handleMusicChanged = useCallback(
    ({ value }) => {
      updateAudioData({ key: "music", value });
    },
    [updateAudioData]
  );

  const handleSoundChanged = useCallback(
    ({ value }) => {
      updateAudioData({ key: "sound", value });
    },
    [updateAudioData]
  );

  const handleLanguageChanged = useCallback((option) => {
    setLanguageValue(option);
  }, []);

  const handleFormalityChanged = useCallback((option) => {
    setFormality(option);
  }, []);

  const handleTextChanged = useCallback(
    (updates) => {
      updateAudioData({ key: "audioText", value: Object.values(updates)[0] || null });
      setUseGen(false);
    },
    [updateAudioData, setUseGen]
  );

  const onAudioUploaded = useCallback(
    ({ url }) => {
      updateAudioData({ key: "url", value: url });
    },
    [updateAudioData]
  );

  // Cloudinary metadata
  const cloudinaryContext = useMemo(() => {
    let context = `orgaId=${orgaId}|source=spotify-audio-generator`;

    if (campaignId) {
      context += `|campaignId=${campaignId}`;
    }

    return context;
  }, [orgaId, campaignId]);

  const selectedMusic = useMemo(
    () => DEFAULT_JINGLES.find(({ value }) => value === music),
    [music]
  );

  const selectedSound = useMemo(() => DEFAULT_SOUNDS.find(({ value }) => value === sound), [sound]);

  const isGenerationDisabled = useMemo(() => {
    const isTextEmpty = !textState || textState.trim() === "";
    const isUnchanged =
      music === prevValues.music &&
      sound === prevValues.sound &&
      url === prevValues.url &&
      languageValue.label === prevValues.language &&
      textState === prevValues.audioText;

    return (
      isTextEmpty ||
      isUnchanged ||
      isGeneratingAudio ||
      isRegeneratingText ||
      textState?.length > limit
    );
  }, [
    textState,
    music,
    sound,
    url,
    isRegeneratingText,
    prevValues,
    isGeneratingAudio,
    languageValue
  ]);

  const onGenerateClip = useCallback(() => {
    const ref = Actions.generateAudioClip.completed.listen((result) => {
      logger.log("AudioLibrary:generateAudio", "completed", result);

      const clipUrl = result?.payload?.url;

      updateAudioData({ key: "url", value: clipUrl });
      setIsGeneratingAudio(false);

      setPrevValues({
        music,
        sound,
        audioText: textState,
        language: languageValue.label,
        url: clipUrl
      });

      Actions.addNotification("Nice, dein neuer Spotify-Clip wurde erzeugt.");
      ref();
    });

    const refFailed = Actions.generateAudioClip.failed.listen((err) => {
      logger.log("error:AudioLibrary:generateAudio", "failed", err);

      const message = ERROR_MAPPING[err?.meta?.message] || DEFAULT_GENERATION_ERROR;
      const translatedMessage = t(`${message}`);
      Actions.addNotification(translatedMessage, "error");

      setIsGeneratingAudio(false);
      refFailed();
    });
    setIsGeneratingAudio(true);

    Actions.generateAudioClip({
      text: textState,
      orgaId,
      tbCampaignId: campaignId,
      background_music: music,
      gender: sound,
      volume_music: 2,
      language: languageValue.value
    });
  }, [music, sound, audioText, updateAudioData, textState, languageValue]);

  const regenerateText = useCallback(() => {
    logger.log("AudioLibrary:regenerateText", "called", {
      channel,
      channelData,
      userProfile
    });

    Actions.setIsRegeneratingText(true);
    updateAudioData({ key: "audioText", value: "" });

    Actions.generateTextRecommendationStream({
      channel: channel,
      jobTitle: channelData.title,
      jobPostingUrl: channelData?.url,
      orgaName: channelData?.companyName,
      orgaId: orgaId || userProfile?.organizationId,
      userId: userProfile?.userId,
      userName: userProfile?.userName,
      language: languageValue.value,
      formality: formality.value
    });
  }, [channelData, orgaId, channel, languageValue, formality]);

  const audioUrl = useMemo(() => {
    if (audioRef?.current) {
      audioRef.current.load();
      audioRef.current.play();
    }
    return url;
  }, [url]);

  return (
    <>
      <SC.TextSectionWrapper>
        <SC.SectionLabel htmlFor="select-text">{t("AUDIO VERSION")}</SC.SectionLabel>
        <SC.TextEditorWrapper id="select-text">
          <CampaignCreateTeaserEditor
            showTeaserRecommendations={false}
            channel={channel}
            updatePost={handleTextChanged}
            teaserText={textState || ""}
            postDataKey="audioText"
            placeholder="Aussage"
            teaserTextStyle={SC.teaserTextStyle}
            isAutoGenerated={isAutoGenerated}
            autoGeneratedText={autoGeneratedText}
            limit={limit}
            smallButtons
            editable
            disableEmojis
          />
        </SC.TextEditorWrapper>
      </SC.TextSectionWrapper>
      <SC.SectionHeader>{t("AUDIO PREFERENCES")}</SC.SectionHeader>
      <SC.SelectOptions>
        <SC.TextSectionWrapper>
          <label htmlFor="select-music">{t("MUSIC")}</label>
          <Select
            inputId="select-music"
            options={DEFAULT_JINGLES}
            onChange={handleMusicChanged}
            value={selectedMusic || DEFAULT_JINGLES[0]}
            styles={SC.AudioGeneratorSelector}
          />
        </SC.TextSectionWrapper>
        <SC.TextSectionWrapper>
          <label htmlFor="select-sound">{t("VOICE")}</label>
          <Select
            inputId="select-sound"
            options={DEFAULT_SOUNDS}
            onChange={handleSoundChanged}
            value={selectedSound || DEFAULT_SOUNDS[0]}
            styles={SC.AudioGeneratorSelector}
          />
        </SC.TextSectionWrapper>
        <SC.TextSectionWrapper>
          <label htmlFor="select-language">{t("LANGUAGE")}</label>
          <Select
            inputId="select-language"
            options={DEFAULT_LANGUAGES}
            onChange={handleLanguageChanged}
            value={languageValue}
            styles={SC.AudioGeneratorSelector}
          />
        </SC.TextSectionWrapper>
        {languageValue.label === "German" && (
          <SC.TextSectionWrapper>
            <label htmlFor="select-formality">{t("FORMALITY")}</label>
            <Select
              inputId="select-formality"
              options={DEFAULT_FORMALITIES}
              onChange={handleFormalityChanged}
              value={formality}
              styles={SC.AudioGeneratorSelector}
            />
          </SC.TextSectionWrapper>
        )}
      </SC.SelectOptions>
      <SC.SectionHeader>{t("Actions")}</SC.SectionHeader>
      <SC.GenerateAudioSection>
        <SC.ButtonRow>
          <Button
            onClick={regenerateText}
            icon={!isRegeneratingText && <i className="fa fa-spotify"></i>}
            disabled={isRegeneratingText}
            variant="secondary"
            style={{ width: "100%" }}
            text={
              isRegeneratingText ? (
                <SC.LoadingIndicatorWrapper>
                  <LoadingIndicator />
                </SC.LoadingIndicatorWrapper>
              ) : (
                t("Regenerate text")
              )
            }
          />

          <Tooltip label={t("Upload clip")}>
            <Upload
              handlePictureChanged={onAudioUploaded}
              showPreview={false}
              showLoading={true}
              allowImages={false}
              allowVideos={false}
              context={cloudinaryContext}
              useUploadIcon
            />
          </Tooltip>
        </SC.ButtonRow>

        <Tooltip
          label={`${t("Make some changes to create a new clip.")} ${
            textState?.length > limit ? t("Text is too long.") : ""
          }`}
          enabled={isGenerationDisabled}
        >
          <Button
            disabled={isGenerationDisabled}
            onClick={onGenerateClip}
            icon={!isGeneratingAudio && <i className="fa fa-spotify"></i>}
            variant="tertiary"
            style={{ width: "100%" }}
            text={
              isGeneratingAudio ? (
                <SC.LoadingIndicatorWrapper>
                  <LoadingIndicator />
                </SC.LoadingIndicatorWrapper>
              ) : (
                t("Generate clip")
              )
            }
          />
        </Tooltip>
      </SC.GenerateAudioSection>
      <SC.TextSectionWrapper>
        <SC.SectionLabel bottom htmlFor="select-preview">
          {t("PREVIEW")}
        </SC.SectionLabel>
        <audio id="select-preview" style={{ width: "100%" }} controls ref={audioRef}>
          <track kind="captions" />
          <source src={audioUrl} type="audio/mp3" />
        </audio>
      </SC.TextSectionWrapper>
    </>
  );
};

export default withRefluxStore(
  AudioGenerator,
  [PreviewStore, CampaignCreationStore],
  ["isRegeneratingText", "userProfile", "locale", "campaignData"]
);
